import { type Object3D, Quaternion, Vector3 } from 'three'

export type Vec3 = [number, number, number]
export type Quat = [number, number, number, number]

export interface Pose {
  position: Vec3
  quaternion: Quat
}

const FORWARD = new Vector3(0, 0, -1)
const MIN_HEIGHT = 0.3
const SPAWN_DROP = 0.1

export function placeInFront(head: Pose, distance: number, drop: number): Pose {
  const q = new Quaternion().fromArray(head.quaternion)
  const dir = FORWARD.clone().applyQuaternion(q)
  dir.y = 0
  if (dir.lengthSq() < 1e-6) dir.set(0, 0, -1)
  dir.normalize()

  const pos = new Vector3().fromArray(head.position).addScaledVector(dir, distance)
  pos.y = Math.max(MIN_HEIGHT, head.position[1] - drop)

  const toUser = new Vector3(-dir.x, 0, -dir.z).normalize()
  const quat = new Quaternion().setFromUnitVectors(FORWARD, toUser)

  return {
    position: [pos.x, pos.y, pos.z],
    quaternion: [quat.x, quat.y, quat.z, quat.w],
  }
}

export function spawnPoseInFront(head: Pose, distance = 0.5): Pose {
  return placeInFront(head, distance, SPAWN_DROP)
}

const FLIP_Y = new Quaternion().setFromAxisAngle(new Vector3(0, 1, 0), Math.PI)

export function menuPose(head: Pose, distance = 0.6): Pose {
  const pose = placeInFront(head, distance, 0)
  const q = new Quaternion().fromArray(pose.quaternion).multiply(FLIP_Y)
  pose.quaternion = [q.x, q.y, q.z, q.w]
  return pose
}

const PLUS_Z = new Vector3(0, 0, 1)

export function faceUserQuat(from: Vec3, headPosition: Vec3): Quat {
  const dir = new Vector3(headPosition[0] - from[0], 0, headPosition[2] - from[2])
  if (dir.lengthSq() < 1e-6) dir.set(0, 0, 1)
  dir.normalize()
  const q = new Quaternion().setFromUnitVectors(PLUS_Z, dir)
  return [q.x, q.y, q.z, q.w]
}

const _p = new Vector3()
const _q = new Quaternion()

export function headPose(obj: Object3D): Pose {
  obj.getWorldPosition(_p)
  obj.getWorldQuaternion(_q)
  return { position: _p.toArray() as Vec3, quaternion: _q.toArray() as Quat }
}
